import { useDebounce } from '@hooks/useDebounce'
import { useSearchExploreTasks } from '@hooks/useSearchTasks'
import { FC, ReactNode } from 'react'
import { NavItem } from '../../../components/NavItem'
import { Column } from '../Column'
import { ActiveFilter } from '../types'
import { getActiveFromFilter } from '../utils/getActiveFromFilter'
import { getItemsAndTotalCount } from '../utils/getItemsAndTotalCount'

interface Props {
  children: ReactNode
  displaySearch?: boolean
  filter?: ActiveFilter
  heading?: string
  integrationId: number
  onActiveFilterChange: (filter: ActiveFilter) => void
  onItemClick: (item: { label: string }) => void
  onItemCountChange: (count?: number) => void
  onSearchChange: (search: string) => void
  onSearchToggle: (displayed: boolean) => void
  path: string[]
  projectId: number
  search?: string
  searchError?: string
  shown: boolean
}

export const ExploreColumn: FC<Props> = ({
  children,
  displaySearch,
  filter,
  heading,
  integrationId,
  onActiveFilterChange,
  onItemClick,
  onItemCountChange,
  onSearchChange,
  onSearchToggle,
  path,
  projectId,
  search = '',
  searchError,
  shown,
}) => {
  const debounced = useDebounce(search)

  const searchResult = useSearchExploreTasks({
    integrationId,
    projectId,
    q: debounced,
    subpath: path,
    onCountChange: onItemCountChange,
    active: getActiveFromFilter(filter),
  })

  if (!shown) return null

  const { items, totalItems } = getItemsAndTotalCount(searchResult)

  return (
    <Column
      displaySearch={displaySearch}
      filter={filter}
      heading={heading}
      isFetching={searchResult.isFetchingNextPage || searchResult.isLoading}
      isInitiallyLoading={searchResult.isLoading}
      items={items}
      loadMoreItems={() => void searchResult.fetchNextPage()}
      activeFilterEnabled={true}
      onActiveFilterChange={onActiveFilterChange}
      onSearchChange={onSearchChange}
      onSearchToggle={onSearchToggle}
      renderItem={(item) => (
        <NavItem
          key={item.label}
          label={item.label}
          onClick={() => onItemClick(item)}
          showImage={false}
        />
      )}
      searchError={searchError}
      searchValue={search}
      totalItems={totalItems}
    >
      {children}
    </Column>
  )
}
